import React, { useEffect } from 'react';
import styled, { DefaultTheme } from 'styled-components';
import { useReleaseNotes } from '../../hooks/useReleaseNotes';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import ReleaseEntry from './ReleaseEntry';
import { DOCS_RELEASE_NOTES_URL } from '../../constants/default-settings';

/**
 * Props for the ReleaseNotesModal component.
 */
interface ReleaseNotesModalProps {
  /** Controls modal visibility */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Styled Components theme */
  theme: DefaultTheme;
}

/**
 * Modal dialog displaying all releases in compact format (version + summary only).
 * Users can view full details by clicking the "View full release notes" link to the docs site.
 *
 * Features:
 * - Displays all releases in scannable list format
 * - Accessible dialog with ARIA attributes and focus management
 * - Multiple close methods: close button, Escape key, backdrop click
 * - Loading and error states
 */

// Backdrop overlay
const Backdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 1000;
  display: ${props => (props.$isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  animation: ${props => (props.$isOpen ? 'fadeIn 0.2s ease-out' : 'none')};

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// Modal container
const ModalContainer = styled.div<{ $isOpen: boolean }>`
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  background: ${props => props.theme.sidePanelBg};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.borderColor}60;
  box-shadow:
    0 20px 40px 0 rgba(0, 0, 0, 0.3),
    0 8px 16px 0 rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${props => (props.$isOpen ? 'slideUp 0.3s cubic-bezier(0.1, 0.9, 0.2, 1)' : 'none')};
  position: relative;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

// Modal header
const ModalHeader = styled.div`
  padding: 24px 28px 20px 28px;
  background: ${props => props.theme.sidePanelHeaderBg}F0;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid ${props => props.theme.borderColor}40;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const ModalTitle = styled.h2`
  color: ${props => props.theme.titleTextColor};
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0;
  line-height: 1.3;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.subtextColor};
  font-size: 28px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.15s cubic-bezier(0.1, 0.9, 0.2, 1);
  outline: none;
  line-height: 1;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover,
  &:focus {
    background: ${props => props.theme.panelBackgroundHover};
    color: ${props => props.theme.textColorHl};
    transform: scale(1.05);
  }

  &:focus {
    box-shadow: 0 0 0 2px ${props => props.theme.primaryColor}60;
  }
`;

// Modal content
const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
  background: ${props => props.theme.sidePanelBg};

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.borderColor}60;
    border-radius: 6px;
    border: 2px solid transparent;
    background-clip: content-box;
    transition: background 0.15s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.primaryColor}80;
    background-clip: content-box;
  }
`;

// Modal footer
const ModalFooter = styled.div`
  padding: 16px 28px 20px 28px;
  background: ${props => props.theme.sidePanelHeaderBg}F0;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid ${props => props.theme.borderColor}40;
  text-align: center;
`;

const ViewAllLink = styled.a`
  color: ${props => props.theme.primaryColor};
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    color: ${props => props.theme.primaryBtnBgdHover};
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid ${props => props.theme.primaryColor}60;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

// Entry divider
const EntryDivider = styled.div`
  height: 1px;
  background: ${props => props.theme.borderColor}40;
  margin: 12px 0;
`;

// Loading state
const LoadingSpinner = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: ${props => props.theme.subtextColor};
  font-size: 15px;

  &:before {
    content: '';
    display: inline-block;
    width: 24px;
    height: 24px;
    border: 3px solid ${props => props.theme.borderColor};
    border-top: 3px solid ${props => props.theme.primaryColor};
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 12px;
    vertical-align: middle;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

// Error message
const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.errorColor};
  background: ${props => props.theme.errorBackground};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.errorColor}30;
  font-size: 15px;
  font-weight: 500;
  margin: 1rem;
`;

// Empty state
const EmptyMessage = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: ${props => props.theme.subtextColor};
  font-size: 15px;
`;

const ReleaseNotesModal: React.FC<ReleaseNotesModalProps> = ({ isOpen, onClose, theme }) => {
  const { releases, loading, error } = useReleaseNotes();
  const focusTrapRef = useFocusTrap(isOpen);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  const hasReleases = releases.length > 0;

  return (
    <Backdrop
      $isOpen={isOpen}
      onClick={handleBackdropClick}
      theme={theme}
      data-testid='release-notes-backdrop'
    >
      <ModalContainer
        ref={focusTrapRef}
        $isOpen={isOpen}
        theme={theme}
        role='dialog'
        aria-modal='true'
        aria-labelledby='release-notes-modal-title'
        data-testid='release-notes-modal'
        onClick={e => e.stopPropagation()}
      >
        <ModalHeader theme={theme}>
          <ModalTitle id='release-notes-modal-title' theme={theme}>
            What's New
          </ModalTitle>
          <CloseButton onClick={onClose} theme={theme} aria-label='Close release notes modal'>
            ×
          </CloseButton>
        </ModalHeader>

        <ModalContent theme={theme}>
          {loading && (
            <LoadingSpinner theme={theme} aria-live='polite'>
              Loading...
            </LoadingSpinner>
          )}

          {error && (
            <ErrorMessage theme={theme} role='alert'>
              <strong>Error:</strong> {error}
            </ErrorMessage>
          )}

          {!loading && !error && !hasReleases && (
            <EmptyMessage theme={theme}>No release notes available.</EmptyMessage>
          )}

          {!loading && !error && hasReleases && (
            <>
              {releases.slice(0, 30).map((release, index, visibleReleases) => {
                const key = `${release.version ?? release.tag_name ?? 'release'}-${index}`;
                return (
                  <React.Fragment key={key}>
                    <ReleaseEntry release={release} theme={theme} compact={true} />
                    {index < visibleReleases.length - 1 && <EntryDivider theme={theme} />}
                  </React.Fragment>
                );
              })}
            </>
          )}
        </ModalContent>

        {!loading && !error && hasReleases && (
          <ModalFooter theme={theme}>
            <ViewAllLink
              href={DOCS_RELEASE_NOTES_URL}
              target='_blank'
              rel='noopener noreferrer'
              theme={theme}
            >
              View full release notes →
            </ViewAllLink>
          </ModalFooter>
        )}
      </ModalContainer>
    </Backdrop>
  );
};

export default ReleaseNotesModal;
